import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Lock, Plus, Edit2, Trash2, Save, X, Eye, EyeOff, Image as ImageIcon,
  Settings, FileText, Briefcase, User, Mail, LogOut, Home, Upload, GraduationCap, Award
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  servicesAPI, 
  projectsAPI, 
  aboutAPI, 
  contactAPI, 
  adminAPI,
  partnersAPI,
  certificatesAPI
} from "../api";

const EditorPanel = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // State for content
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [aboutContent, setAboutContent] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [certificates, setCertificates] = useState([]);
  
  // Edit modals
  const [editingService, setEditingService] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [isPartnerDialogOpen, setIsPartnerDialogOpen] = useState(false);
  const [isCertificateDialogOpen, setIsCertificateDialogOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Compress and convert image to base64 data URL.
  // Resizes to max 1600px wide JPEG @ 0.82 quality so DB stays lean.
  const compressImageToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error("Failed to load image"));
        img.onload = () => {
          const MAX_W = 1600;
          let { width, height } = img;
          if (width > MAX_W) {
            height = Math.round(height * (MAX_W / width));
            width = MAX_W;
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.82));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e, setEntity) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file",
        description: "Please choose an image file (JPG, PNG, etc.)",
        variant: "destructive",
      });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({
        title: "Image too large",
        description: "Please choose an image under 15 MB",
        variant: "destructive",
      });
      return;
    }
    try {
      setUploadingImage(true);
      const dataUrl = await compressImageToDataUrl(file);
      setEntity((prev) => ({ ...prev, image: dataUrl }));
      toast({
        title: "Photo Uploaded",
        description: "Click 'Save' to publish it.",
      });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err.message || "Could not process the image",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  // Wrapper for logos that store the data URL in a "logo" field (not "image")
  const handleLogoUpload = async (e, setEntity) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please choose an image file", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "Logo too large", description: "Please choose under 10 MB", variant: "destructive" });
      return;
    }
    try {
      setUploadingImage(true);
      const dataUrl = await compressImageToDataUrl(file);
      setEntity((prev) => ({ ...prev, logo: dataUrl }));
      toast({ title: "Logo Uploaded", description: "Click 'Save' to publish." });
    } catch (err) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleProjectImageUpload = (e) => handleImageUpload(e, setEditingProject);
  const handleCertificateImageUpload = (e) => handleImageUpload(e, setEditingCertificate);
  const handlePartnerLogoUpload = (e) => handleLogoUpload(e, setEditingPartner);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    if (adminAPI.isAuthenticated()) {
      try {
        await adminAPI.verify();
        setIsAuthenticated(true);
        loadAllContent();
      } catch (error) {
        setIsAuthenticated(false);
        adminAPI.logout();
      }
    }
  };

  const loadAllContent = async () => {
    try {
      setLoading(true);
      const [servicesData, projectsData, aboutData, contactsData, partnersData, certsData] = await Promise.all([
        servicesAPI.getAll(),
        projectsAPI.getAll(),
        aboutAPI.get(),
        contactAPI.getAll().catch(() => []),
        partnersAPI.getAll().catch(() => []),
        certificatesAPI.getAll().catch(() => []),
      ]);
      
      setServices(servicesData);
      setProjects(projectsData);
      setAboutContent(aboutData);
      setContacts(contactsData);
      setPartners(partnersData);
      setCertificates(certsData);
    } catch (error) {
      console.error("Error loading content:", error);
      toast({
        title: "Error",
        description: "Failed to load content",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminAPI.login(password);
      setIsAuthenticated(true);
      toast({
        title: "Login Successful",
        description: "Welcome to the Editor Panel",
      });
      loadAllContent();
    } catch (error) {
      toast({
        title: "Login Failed",
        description: "Incorrect password",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminAPI.logout();
    setIsAuthenticated(false);
    setPassword("");
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully",
    });
  };

  // Service Management
  const handleAddService = () => {
    setEditingService({
      title: "",
      description: "",
      icon: "Code",
      features: ["", "", "", ""]
    });
    setIsServiceDialogOpen(true);
  };

  const handleEditService = (service) => {
    setEditingService({ ...service });
    setIsServiceDialogOpen(true);
  };

  const handleSaveService = async () => {
    setLoading(true);
    try {
      if (services.find(s => s.id === editingService.id)) {
        await servicesAPI.update(editingService.id, editingService);
        toast({
          title: "Service Updated",
          description: "Service has been updated successfully",
        });
      } else {
        await servicesAPI.create(editingService);
        toast({
          title: "Service Added",
          description: "New service has been added successfully",
        });
      }
      setIsServiceDialogOpen(false);
      setEditingService(null);
      loadAllContent();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save service",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    
    setLoading(true);
    try {
      await servicesAPI.delete(id);
      toast({
        title: "Service Deleted",
        description: "Service has been deleted successfully",
      });
      loadAllContent();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete service",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Project Management
  const handleAddProject = () => {
    setEditingProject({
      title: "",
      category: "",
      description: "",
      image: "",
      tags: ["", "", ""],
      year: new Date().getFullYear().toString()
    });
    setIsProjectDialogOpen(true);
  };

  const handleEditProject = (project) => {
    setEditingProject({ ...project });
    setIsProjectDialogOpen(true);
  };

  const handleSaveProject = async () => {
    setLoading(true);
    try {
      if (projects.find(p => p.id === editingProject.id)) {
        await projectsAPI.update(editingProject.id, editingProject);
        toast({
          title: "Project Updated",
          description: "Project has been updated successfully",
        });
      } else {
        await projectsAPI.create(editingProject);
        toast({
          title: "Project Added",
          description: "New project has been added successfully",
        });
      }
      setIsProjectDialogOpen(false);
      setEditingProject(null);
      loadAllContent();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save project",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    
    setLoading(true);
    try {
      await projectsAPI.delete(id);
      toast({
        title: "Project Deleted",
        description: "Project has been deleted successfully",
      });
      loadAllContent();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Partner (Learning Logo) Management
  const handleAddPartner = () => {
    setEditingPartner({ name: "", logo: "", website: "", courses: "", order: (partners?.length || 0) + 1 });
    setIsPartnerDialogOpen(true);
  };

  const handleEditPartner = (partner) => {
    setEditingPartner({ ...partner });
    setIsPartnerDialogOpen(true);
  };

  const handleSavePartner = async () => {
    if (!editingPartner.name?.trim() || !editingPartner.logo?.trim()) {
      toast({ title: "Missing info", description: "Name and logo are required", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (partners.find((p) => p.id === editingPartner.id)) {
        await partnersAPI.update(editingPartner.id, editingPartner);
        toast({ title: "Logo Updated", description: "Learning partner updated successfully" });
      } else {
        await partnersAPI.create(editingPartner);
        toast({ title: "Logo Added", description: "New learning partner added" });
      }
      setIsPartnerDialogOpen(false);
      setEditingPartner(null);
      loadAllContent();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save logo", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePartner = async (id) => {
    if (!window.confirm("Delete this learning logo?")) return;
    setLoading(true);
    try {
      await partnersAPI.delete(id);
      toast({ title: "Deleted", description: "Logo removed successfully" });
      loadAllContent();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete logo", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Certificate Management
  const handleAddCertificate = () => {
    setEditingCertificate({
      name: "",
      issuer: "",
      year: new Date().getFullYear().toString(),
      image: "",
      link: "",
      order: (certificates?.length || 0) + 1,
    });
    setIsCertificateDialogOpen(true);
  };

  const handleEditCertificate = (cert) => {
    setEditingCertificate({ ...cert });
    setIsCertificateDialogOpen(true);
  };

  const handleSaveCertificate = async () => {
    if (!editingCertificate.name?.trim() || !editingCertificate.issuer?.trim() || !editingCertificate.image?.trim()) {
      toast({ title: "Missing info", description: "Name, issuer and photo are required", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (certificates.find((c) => c.id === editingCertificate.id)) {
        await certificatesAPI.update(editingCertificate.id, editingCertificate);
        toast({ title: "Certificate Updated", description: "Certificate updated successfully" });
      } else {
        await certificatesAPI.create(editingCertificate);
        toast({ title: "Certificate Added", description: "New certificate added" });
      }
      setIsCertificateDialogOpen(false);
      setEditingCertificate(null);
      loadAllContent();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save certificate", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCertificate = async (id) => {
    if (!window.confirm("Delete this certificate?")) return;
    setLoading(true);
    try {
      await certificatesAPI.delete(id);
      toast({ title: "Deleted", description: "Certificate removed" });
      loadAllContent();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete certificate", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // About Content Management
  const handleSaveAbout = async () => {
    setLoading(true);
    try {
      await aboutAPI.update(aboutContent);
      toast({
        title: "About Content Updated",
        description: "About page content has been updated successfully",
      });
      loadAllContent();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update about content",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen editor-login-bg flex items-center justify-center p-4">
        <Card className="w-full max-w-md editor-login-card shadow-2xl">
          <CardHeader className="text-center space-y-4">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-orange-500 rounded-2xl flex items-center justify-center mx-auto animate-glow">
              <Lock className="text-white" size={40} />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold gradient-text">Editor Panel</CardTitle>
              <p className="text-sm text-gray-600 mt-2">Karthipan Karunamoorthy | Freelancer</p>
              <p className="text-xs text-gray-500 mt-1">Full Content Management Access</p>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter editor password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="border-2 pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold"
                disabled={loading}
              >
                {loading ? "Logging in..." : "Login to Editor"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen editor-page bg-black">
      {/* Header */}
      <div className="editor-header sticky top-0 z-40 shadow-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
                <Settings className="text-black" size={24} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Content Editor</h1>
                <p className="text-sm text-yellow-500/80">Karthipan Karunamoorthy | Freelancer</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Home size={18} />
                <span>Visit Site</span>
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="flex items-center space-x-2 text-red-600 hover:bg-red-50"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && !services.length ? (
          <div className="text-center py-20">
            <div className="animate-spin w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading content...</p>
          </div>
        ) : (
          <Tabs defaultValue="services" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 h-auto p-2 gap-1">
              <TabsTrigger value="services" className="flex items-center space-x-2 py-3" data-testid="tab-services">
                <Briefcase size={18} />
                <span className="hidden sm:inline">Services</span>
                <Badge className="ml-1">{services.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="portfolio" className="flex items-center space-x-2 py-3" data-testid="tab-portfolio">
                <FileText size={18} />
                <span className="hidden sm:inline">Portfolio</span>
                <Badge className="ml-1">{projects.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="partners" className="flex items-center space-x-2 py-3" data-testid="tab-partners">
                <GraduationCap size={18} />
                <span className="hidden sm:inline">Learning</span>
                <Badge className="ml-1">{partners.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="certificates" className="flex items-center space-x-2 py-3" data-testid="tab-certificates">
                <Award size={18} />
                <span className="hidden sm:inline">Certs</span>
                <Badge className="ml-1">{certificates.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="about" className="flex items-center space-x-2 py-3" data-testid="tab-about">
                <User size={18} />
                <span className="hidden sm:inline">About</span>
              </TabsTrigger>
              <TabsTrigger value="contacts" className="flex items-center space-x-2 py-3" data-testid="tab-contacts">
                <Mail size={18} />
                <span className="hidden sm:inline">Contacts</span>
                <Badge className="ml-1">{contacts.length}</Badge>
              </TabsTrigger>
            </TabsList>

            {/* Services Tab */}
            <TabsContent value="services" className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Manage Services</h2>
                  <p className="text-gray-600 mt-1">Add, edit, or remove services you offer as a freelancer</p>
                </div>
                <Button 
                  onClick={handleAddService} 
                  className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
                  size="lg"
                >
                  <Plus className="mr-2" size={20} /> Add New Service
                </Button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service) => (
                  <Card key={service.id} className="editor-card border-2 hover:border-yellow-400 transition-all duration-300 hover-lift">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                          <Badge variant="outline" className="text-xs">{service.icon}</Badge>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditService(service)}
                            className="hover:bg-blue-50"
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteService(service.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{service.description}</p>
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-700 uppercase">Features:</p>
                        {service.features.map((feature, idx) => (
                          <p key={idx} className="text-xs text-gray-500">• {feature}</p>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Portfolio Tab */}
            <TabsContent value="portfolio" className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Manage Portfolio</h2>
                  <p className="text-gray-600 mt-1">Showcase your best work and add your real project photos</p>
                </div>
                <Button 
                  onClick={handleAddProject} 
                  className="bg-orange-500 hover:bg-orange-600 text-white shadow-lg"
                  size="lg"
                >
                  <Plus className="mr-2" size={20} /> Add New Project
                </Button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project) => (
                  <Card key={project.id} className="editor-card border-2 overflow-hidden hover:border-yellow-400 transition-all duration-300 hover-lift">
                    <div className="relative h-48 bg-gray-200">
                      <img 
                        src={project.image} 
                        alt={project.title} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/400x300?text=No+Image";
                        }}
                      />
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-white/90 text-gray-900 backdrop-blur-sm">{project.year}</Badge>
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <Badge variant="outline" className="text-xs mb-2 text-orange-600 border-orange-600">
                            {project.category}
                          </Badge>
                          <h3 className="font-bold text-gray-900 text-lg">{project.title}</h3>
                        </div>
                        <div className="flex space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditProject(project)}
                            className="hover:bg-orange-50"
                          >
                            <Edit2 size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteProject(project.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mb-3 line-clamp-2">{project.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {project.tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Learning Partners Tab */}
            <TabsContent value="partners" className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Learning Platform Logos</h2>
                  <p className="text-gray-600 mt-1">Add and manage the scrolling learning platform logos on your homepage</p>
                </div>
                <Button
                  onClick={handleAddPartner}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black shadow-lg font-bold"
                  size="lg"
                  data-testid="add-partner-btn"
                >
                  <Plus className="mr-2" size={20} /> Add New Logo
                </Button>
              </div>

              {partners.length === 0 ? (
                <Card className="editor-card border-2">
                  <CardContent className="p-12 text-center">
                    <GraduationCap className="mx-auto text-yellow-500 mb-4" size={64} />
                    <h3 className="text-xl font-bold text-white mb-2">No logos yet</h3>
                    <p className="text-gray-400">Click "Add New Logo" to add your first learning platform</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {partners.map((partner) => (
                    <Card key={partner.id} className="editor-card border-2 hover:border-yellow-400 transition-all duration-300 hover-lift" data-testid="partner-card">
                      <CardContent className="p-6">
                        <div className="h-24 mb-4 flex items-center justify-center bg-[#1a1a1a] rounded-lg p-3">
                          <img
                            src={partner.logo}
                            alt={partner.name}
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/200x80?text=" + encodeURIComponent(partner.name);
                            }}
                          />
                        </div>
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-lg font-bold text-white">{partner.name}</h3>
                          <div className="flex space-x-1">
                            <Button size="sm" variant="outline" onClick={() => handleEditPartner(partner)}>
                              <Edit2 size={14} />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDeletePartner(partner.id)} className="text-red-600">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                        {partner.courses && (
                          <p className="text-xs text-gray-400 line-clamp-2">{partner.courses}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Certificates Tab */}
            <TabsContent value="certificates" className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">My Certifications</h2>
                  <p className="text-gray-600 mt-1">Upload certificates to display them as a scrolling showcase on your site</p>
                </div>
                <Button
                  onClick={handleAddCertificate}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black shadow-lg font-bold"
                  size="lg"
                  data-testid="add-certificate-btn"
                >
                  <Plus className="mr-2" size={20} /> Add New Certificate
                </Button>
              </div>

              {certificates.length === 0 ? (
                <Card className="editor-card border-2">
                  <CardContent className="p-12 text-center">
                    <Award className="mx-auto text-yellow-500 mb-4" size={64} />
                    <h3 className="text-xl font-bold text-white mb-2">No certificates yet</h3>
                    <p className="text-gray-400">Click "Add New Certificate" to showcase your first credential</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {certificates.map((cert) => (
                    <Card key={cert.id} className="editor-card border-2 overflow-hidden hover:border-yellow-400 transition-all duration-300 hover-lift" data-testid="certificate-card">
                      <div className="h-40 bg-[#1a1a1a]">
                        <img
                          src={cert.image}
                          alt={cert.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/400x200?text=" + encodeURIComponent(cert.name);
                          }}
                        />
                      </div>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="font-bold text-white">{cert.name}</h3>
                            <p className="text-yellow-400 text-xs mt-1">{cert.issuer}</p>
                          </div>
                          <Badge className="bg-yellow-500 text-black">{cert.year}</Badge>
                        </div>
                        <div className="flex space-x-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => handleEditCertificate(cert)} className="flex-1">
                            <Edit2 size={14} className="mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteCertificate(cert.id)} className="text-red-600">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* About Tab */}
            <TabsContent value="about" className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Manage About Content</h2>
                  <p className="text-gray-600 mt-1">Update your story and freelance profile information</p>
                </div>
                <Button 
                  onClick={handleSaveAbout} 
                  className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
                  size="lg"
                  disabled={loading}
                >
                  <Save className="mr-2" size={20} /> {loading ? "Saving..." : "Save Changes"}
                </Button>
              </div>

              {aboutContent && (
                <div className="grid gap-6 form-card">
                  <Card className="editor-card border-2">
                    <CardHeader>
                      <CardTitle>Biography</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Bio</Label>
                        <Textarea
                          value={aboutContent.bio}
                          onChange={(e) => setAboutContent({ ...aboutContent, bio: e.target.value })}
                          rows={5}
                          className="border-2"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Mission Statement</Label>
                          <Textarea
                            value={aboutContent.mission}
                            onChange={(e) => setAboutContent({ ...aboutContent, mission: e.target.value })}
                            rows={4}
                            className="border-2"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Vision Statement</Label>
                          <Textarea
                            value={aboutContent.vision}
                            onChange={(e) => setAboutContent({ ...aboutContent, vision: e.target.value })}
                            rows={4}
                            className="border-2"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="editor-card border-2">
                    <CardHeader>
                      <CardTitle>Statistics</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label>Experience</Label>
                          <Input
                            value={aboutContent.experience}
                            onChange={(e) => setAboutContent({ ...aboutContent, experience: e.target.value })}
                            className="border-2"
                            placeholder="e.g., 8+ Years"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Projects Completed</Label>
                          <Input
                            value={aboutContent.projectsCompleted}
                            onChange={(e) => setAboutContent({ ...aboutContent, projectsCompleted: e.target.value })}
                            className="border-2"
                            placeholder="e.g., 150+"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Happy Clients</Label>
                          <Input
                            value={aboutContent.happyClients}
                            onChange={(e) => setAboutContent({ ...aboutContent, happyClients: e.target.value })}
                            className="border-2"
                            placeholder="e.g., 80+"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Awards Won</Label>
                          <Input
                            value={aboutContent.awards}
                            onChange={(e) => setAboutContent({ ...aboutContent, awards: e.target.value })}
                            className="border-2"
                            placeholder="e.g., 12"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="editor-card border-2">
                    <CardHeader>
                      <CardTitle>Skills</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {aboutContent.skills.map((skill, index) => (
                          <div key={index} className="grid md:grid-cols-2 gap-4 items-center">
                            <Input
                              value={skill.name}
                              onChange={(e) => {
                                const newSkills = [...aboutContent.skills];
                                newSkills[index].name = e.target.value;
                                setAboutContent({ ...aboutContent, skills: newSkills });
                              }}
                              placeholder="Skill name"
                              className="border-2"
                            />
                            <div className="flex items-center space-x-4">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={skill.level}
                                onChange={(e) => {
                                  const newSkills = [...aboutContent.skills];
                                  newSkills[index].level = parseInt(e.target.value) || 0;
                                  setAboutContent({ ...aboutContent, skills: newSkills });
                                }}
                                className="border-2"
                              />
                              <span className="text-sm text-gray-600 whitespace-nowrap">{skill.level}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts" className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Contact Submissions</h2>
                <p className="text-gray-600 mt-1">View messages from your website visitors</p>
              </div>

              {contacts.length === 0 ? (
                <Card className="editor-card border-2">
                  <CardContent className="p-12 text-center">
                    <Mail className="mx-auto text-gray-400 mb-4" size={64} />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No Contact Submissions Yet</h3>
                    <p className="text-gray-600">When visitors submit the contact form, messages will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {contacts.map((contact) => (
                    <Card key={contact.id} className="editor-card border-2 hover:border-yellow-400 transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{contact.name}</h3>
                            <p className="text-sm text-gray-600">{contact.email}</p>
                            {contact.phone && (
                              <p className="text-sm text-gray-600">{contact.phone}</p>
                            )}
                          </div>
                          <div className="text-right">
                            {contact.service && (
                              <Badge className="mb-2">{contact.service}</Badge>
                            )}
                            <p className="text-xs text-gray-500">
                              {new Date(contact.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{contact.message}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Service Edit Dialog */}
      <Dialog open={isServiceDialogOpen} onOpenChange={setIsServiceDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingService && services.find(s => s.id === editingService.id) ? "Edit Service" : "Add New Service"}
            </DialogTitle>
          </DialogHeader>
          {editingService && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Service Title *</Label>
                <Input
                  value={editingService.title}
                  onChange={(e) => setEditingService({ ...editingService, title: e.target.value })}
                  className="border-2"
                  placeholder="e.g., Video Editing"
                />
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={editingService.description}
                  onChange={(e) => setEditingService({ ...editingService, description: e.target.value })}
                  rows={4}
                  className="border-2"
                  placeholder="Describe the service..."
                />
              </div>
              <div className="space-y-2">
                <Label>Icon Name (Lucide React)</Label>
                <Input
                  value={editingService.icon}
                  onChange={(e) => setEditingService({ ...editingService, icon: e.target.value })}
                  className="border-2"
                  placeholder="e.g., Code, Video, Shield, Palette"
                />
                <p className="text-xs text-gray-500">Use Lucide React icon names like: Video, Code, Shield, Palette, etc.</p>
              </div>
              <div className="space-y-2">
                <Label>Features (4 items)</Label>
                {editingService.features.map((feature, idx) => (
                  <Input
                    key={idx}
                    value={feature}
                    onChange={(e) => {
                      const newFeatures = [...editingService.features];
                      newFeatures[idx] = e.target.value;
                      setEditingService({ ...editingService, features: newFeatures });
                    }}
                    placeholder={`Feature ${idx + 1}`}
                    className="border-2"
                  />
                ))}
              </div>
              <div className="flex space-x-3 pt-4">
                <Button 
                  onClick={handleSaveService} 
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={loading}
                >
                  <Save className="mr-2" size={18} /> {loading ? "Saving..." : "Save Service"}
                </Button>
                <Button 
                  onClick={() => setIsServiceDialogOpen(false)} 
                  variant="outline" 
                  className="flex-1"
                >
                  <X className="mr-2" size={18} /> Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Edit Dialog */}
      <Dialog open={isProjectDialogOpen} onOpenChange={setIsProjectDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingProject && projects.find(p => p.id === editingProject.id) ? "Edit Project" : "Add New Project"}
            </DialogTitle>
          </DialogHeader>
          {editingProject && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Project Title *</Label>
                <Input
                  value={editingProject.title}
                  onChange={(e) => setEditingProject({ ...editingProject, title: e.target.value })}
                  className="border-2"
                  placeholder="e.g., E-Commerce Platform Redesign"
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Input
                  value={editingProject.category}
                  onChange={(e) => setEditingProject({ ...editingProject, category: e.target.value })}
                  placeholder="e.g., Full Stack Development, Graphic Design"
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={editingProject.description}
                  onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                  rows={4}
                  className="border-2"
                  placeholder="Describe the project..."
                />
              </div>
              <div className="space-y-2">
                <Label>Project Photo *</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <label
                    htmlFor="project-image-upload"
                    data-testid="project-image-upload-label"
                    className={`flex-1 cursor-pointer inline-flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-yellow-500/60 bg-yellow-500/5 hover:bg-yellow-500/10 transition px-4 py-3 text-sm font-semibold text-yellow-600 ${uploadingImage ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    <Upload size={18} />
                    {uploadingImage ? "Processing photo..." : "Upload Photo from Device"}
                  </label>
                  <input
                    id="project-image-upload"
                    data-testid="project-image-upload-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleProjectImageUpload}
                  />
                  {editingProject.image && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditingProject({ ...editingProject, image: "" })}
                      className="text-red-600 hover:bg-red-50"
                      data-testid="project-image-remove-btn"
                    >
                      <Trash2 size={16} className="mr-1" /> Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Tip: Tap to pick a photo from your phone gallery or computer. Or paste an image URL below.
                </p>
                <Input
                  value={editingProject.image?.startsWith("data:") ? "" : editingProject.image}
                  onChange={(e) => setEditingProject({ ...editingProject, image: e.target.value })}
                  placeholder="…or paste an image URL (https://…)"
                  className="border-2"
                  data-testid="project-image-url-input"
                />
                {editingProject.image && (
                  <div className="mt-2 border-2 rounded-lg overflow-hidden">
                    <img 
                      src={editingProject.image} 
                      alt="Preview" 
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/400x300?text=Invalid+Image+URL";
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Year *</Label>
                <Input
                  value={editingProject.year}
                  onChange={(e) => setEditingProject({ ...editingProject, year: e.target.value })}
                  placeholder="2024"
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={editingProject.tags.join(", ")}
                  onChange={(e) => setEditingProject({ ...editingProject, tags: e.target.value.split(",").map(t => t.trim()) })}
                  placeholder="React, Node.js, MongoDB, etc."
                  className="border-2"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <Button 
                  onClick={handleSaveProject} 
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  disabled={loading}
                >
                  <Save className="mr-2" size={18} /> {loading ? "Saving..." : "Save Project"}
                </Button>
                <Button 
                  onClick={() => setIsProjectDialogOpen(false)} 
                  variant="outline" 
                  className="flex-1"
                >
                  <X className="mr-2" size={18} /> Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Partner (Learning Logo) Edit Dialog */}
      <Dialog open={isPartnerDialogOpen} onOpenChange={setIsPartnerDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingPartner && partners.find((p) => p.id === editingPartner.id) ? "Edit Learning Logo" : "Add New Learning Logo"}
            </DialogTitle>
          </DialogHeader>
          {editingPartner && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Platform Name *</Label>
                <Input
                  value={editingPartner.name}
                  onChange={(e) => setEditingPartner({ ...editingPartner, name: e.target.value })}
                  placeholder="e.g., Coursera, Skill Dragon"
                  className="border-2"
                  data-testid="partner-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Logo *</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <label
                    htmlFor="partner-logo-upload"
                    className={`flex-1 cursor-pointer inline-flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-yellow-500/60 bg-yellow-500/5 hover:bg-yellow-500/10 transition px-4 py-3 text-sm font-semibold text-yellow-600 ${uploadingImage ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    <Upload size={18} />
                    {uploadingImage ? "Processing..." : "Upload Logo from Device"}
                  </label>
                  <input
                    id="partner-logo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePartnerLogoUpload}
                    data-testid="partner-logo-upload-input"
                  />
                  {editingPartner.logo && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditingPartner({ ...editingPartner, logo: "" })}
                      className="text-red-600"
                    >
                      <Trash2 size={16} className="mr-1" /> Remove
                    </Button>
                  )}
                </div>
                <Input
                  value={editingPartner.logo?.startsWith("data:") ? "" : editingPartner.logo}
                  onChange={(e) => setEditingPartner({ ...editingPartner, logo: e.target.value })}
                  placeholder="…or paste a logo URL (https://…)"
                  className="border-2"
                  data-testid="partner-logo-url-input"
                />
                {editingPartner.logo && (
                  <div className="mt-2 border-2 rounded-lg overflow-hidden bg-[#1a1a1a] p-3">
                    <img
                      src={editingPartner.logo}
                      alt="Preview"
                      className="w-full h-32 object-contain"
                      onError={(e) => { e.target.src = "https://via.placeholder.com/300x120?text=Invalid+URL"; }}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Website Link</Label>
                <Input
                  value={editingPartner.website || ""}
                  onChange={(e) => setEditingPartner({ ...editingPartner, website: e.target.value })}
                  placeholder="https://example.com"
                  className="border-2"
                  data-testid="partner-website-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Courses / Skills Learned</Label>
                <Textarea
                  value={editingPartner.courses || ""}
                  onChange={(e) => setEditingPartner({ ...editingPartner, courses: e.target.value })}
                  rows={3}
                  placeholder="e.g., Video Editing, UI/UX, Full Stack Development"
                  className="border-2"
                  data-testid="partner-courses-input"
                />
                <p className="text-xs text-gray-500">Shown on hover over the logo</p>
              </div>

              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={editingPartner.order}
                  onChange={(e) => setEditingPartner({ ...editingPartner, order: parseInt(e.target.value) || 0 })}
                  placeholder="1"
                  className="border-2"
                />
                <p className="text-xs text-gray-500">Lower numbers appear first in the scrolling strip</p>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button onClick={handleSavePartner} className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-bold" disabled={loading} data-testid="save-partner-btn">
                  <Save className="mr-2" size={18} /> {loading ? "Saving..." : "Save Logo"}
                </Button>
                <Button onClick={() => setIsPartnerDialogOpen(false)} variant="outline" className="flex-1">
                  <X className="mr-2" size={18} /> Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Certificate Edit Dialog */}
      <Dialog open={isCertificateDialogOpen} onOpenChange={setIsCertificateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingCertificate && certificates.find((c) => c.id === editingCertificate.id) ? "Edit Certificate" : "Add New Certificate"}
            </DialogTitle>
          </DialogHeader>
          {editingCertificate && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Certificate Name *</Label>
                <Input
                  value={editingCertificate.name}
                  onChange={(e) => setEditingCertificate({ ...editingCertificate, name: e.target.value })}
                  placeholder="e.g., Google Digital Marketing"
                  className="border-2"
                  data-testid="certificate-name-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Issuer *</Label>
                  <Input
                    value={editingCertificate.issuer}
                    onChange={(e) => setEditingCertificate({ ...editingCertificate, issuer: e.target.value })}
                    placeholder="e.g., Coursera, Google"
                    className="border-2"
                    data-testid="certificate-issuer-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Year *</Label>
                  <Input
                    value={editingCertificate.year}
                    onChange={(e) => setEditingCertificate({ ...editingCertificate, year: e.target.value })}
                    placeholder="2024"
                    className="border-2"
                    data-testid="certificate-year-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Certificate Photo *</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <label
                    htmlFor="cert-image-upload"
                    className={`flex-1 cursor-pointer inline-flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-yellow-500/60 bg-yellow-500/5 hover:bg-yellow-500/10 transition px-4 py-3 text-sm font-semibold text-yellow-600 ${uploadingImage ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    <Upload size={18} />
                    {uploadingImage ? "Processing..." : "Upload Certificate Photo"}
                  </label>
                  <input
                    id="cert-image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleCertificateImageUpload}
                    data-testid="cert-image-upload-input"
                  />
                  {editingCertificate.image && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditingCertificate({ ...editingCertificate, image: "" })}
                      className="text-red-600"
                    >
                      <Trash2 size={16} className="mr-1" /> Remove
                    </Button>
                  )}
                </div>
                <Input
                  value={editingCertificate.image?.startsWith("data:") ? "" : editingCertificate.image}
                  onChange={(e) => setEditingCertificate({ ...editingCertificate, image: e.target.value })}
                  placeholder="…or paste an image URL (https://…)"
                  className="border-2"
                  data-testid="cert-image-url-input"
                />
                {editingCertificate.image && (
                  <div className="mt-2 border-2 rounded-lg overflow-hidden">
                    <img
                      src={editingCertificate.image}
                      alt="Preview"
                      className="w-full h-48 object-cover"
                      onError={(e) => { e.target.src = "https://via.placeholder.com/400x250?text=Invalid+URL"; }}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Credential Verification Link</Label>
                <Input
                  value={editingCertificate.link || ""}
                  onChange={(e) => setEditingCertificate({ ...editingCertificate, link: e.target.value })}
                  placeholder="https://credential-url.com"
                  className="border-2"
                  data-testid="certificate-link-input"
                />
                <p className="text-xs text-gray-500">Optional — clicking the certificate opens this link</p>
              </div>

              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={editingCertificate.order}
                  onChange={(e) => setEditingCertificate({ ...editingCertificate, order: parseInt(e.target.value) || 0 })}
                  className="border-2"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <Button onClick={handleSaveCertificate} className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-bold" disabled={loading} data-testid="save-certificate-btn">
                  <Save className="mr-2" size={18} /> {loading ? "Saving..." : "Save Certificate"}
                </Button>
                <Button onClick={() => setIsCertificateDialogOpen(false)} variant="outline" className="flex-1">
                  <X className="mr-2" size={18} /> Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditorPanel;
